import React from 'react';
import { Box, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';
import tokens from '../../styles/tokens';
import SeverityBadge from './SeverityBadge';
import { countFormatterWithCommas, dataSizeFormatter, dateFormatter } from '../../utils/helpers';

const InfoRow = ({ label, value, valueColor }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
    <Typography variant="body2" sx={{ color: tokens.colors.textMuted }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color: valueColor || 'inherit' }}>
      {value ?? '--'}
    </Typography>
  </Box>
);

const NotificationDetailsCard = ({ notification, variant = 'card' }) => {
  const cardSx = variant === 'drawer'
    ? { boxShadow: 'none', borderRadius: 0 }
    : { margin: tokens.spacing.cardMargin, boxShadow: tokens.colors.shadow };

  if (!notification) {
    return (
      <Card sx={cardSx}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Notification Details
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.colors.textMuted, marginTop: 1 }}>
            Select a notification to inspect metrics, thresholds, and scoring.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const metrics = notification.alertDetails?.metricsUsed || {};
  const thresholds = notification.alertDetails?.thresholdsUsed || {};
  const breakdown = notification.alertDetails?.weightBreakdown || {};
  const penalties = notification.alertDetails?.penalties || {};
  const scoreValue = breakdown.finalScore ?? notification.alertDetails?.performanceScore ?? notification.performanceScore ?? '--';
  const severityColor = '#5e35b1';
  const hasAlertDetails = Boolean(
    Object.keys(metrics).length
    || Object.keys(thresholds).length
    || Object.keys(breakdown).length
    || Object.keys(penalties).length
  );

  return (
    <Card sx={cardSx}>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {notification.cluster}
            </Typography>
          </Box>

          <Stack spacing={1}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <SeverityBadge severity={notification.severity} />
              <Chip label={notification.type} size="small" />
            </Box>
            <Box
              sx={{
                backgroundColor: '#f5f6fb',
                borderRadius: 1,
                padding: 1.5
              }}
            >
              <Typography variant="body2" sx={{ color: tokens.colors.textMuted }}>
                Summary
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, marginTop: 0.5 }}>
                {notification.type} performance is {notification.severity}.
              </Typography>
              <Typography variant="body2" sx={{ marginTop: 0.5 }}>
                Performance Score:{' '}
                <Box component="span" sx={{ fontWeight: 700, color: '#1a73e8' }}>
                  {scoreValue}/100
                </Box>
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-line',
                color: tokens.colors.textMuted,
                lineHeight: 1.6
              }}
            >
              {notification.message}
            </Typography>
          </Stack>

          {hasAlertDetails ? (
            <>
              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Scoring Breakdown
                </Typography>
                <InfoRow
                  label="Performance Score"
                  value={breakdown.finalScore ?? notification.alertDetails?.performanceScore ?? notification.performanceScore}
                  valueColor="#1a73e8"
                />
                <InfoRow label="Count Penalty" value={breakdown.countPenalty} />
                <InfoRow label="Size Penalty" value={breakdown.sizePenalty} />
                <InfoRow label="Combined Penalty" value={breakdown.combinedPenalty} />
                <InfoRow label="Skip Multiplier" value={breakdown.skipMultiplier} valueColor="#5e35b1" />
                <InfoRow label="Rescan Load Multiplier" value={breakdown.rescanLoadMultiplier} valueColor="#5e35b1" />
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Penalties Applied
                </Typography>
                <InfoRow label="Skip Multiplier" value={penalties.skipMultiplier} valueColor="#c62828" />
                <InfoRow label="Rescan Load Multiplier" value={penalties.rescanLoadMultiplier} valueColor="#c62828" />
                <InfoRow label="Combined Multiplier" value={penalties.combinedMultiplier} valueColor="#c62828" />
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Performance Snapshot (24h)
                </Typography>
                <InfoRow label="Processed Count" value={countFormatterWithCommas(metrics.processedCount24h)} valueColor="#1a73e8" />
                <InfoRow label="Processed Size" value={dataSizeFormatter(metrics.processedSizeGb24h)} valueColor="#1a73e8" />
                <InfoRow label="Skipped Count" value={countFormatterWithCommas(metrics.skippedCount24h)} valueColor="#5e35b1" />
                <InfoRow label="Rescan Pending" value={countFormatterWithCommas(metrics.rescanPendingCurrent)} valueColor="#5e35b1" />
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Thresholds Used
                </Typography>
                <InfoRow
                  label="Expected Files/vCPU"
                  value={countFormatterWithCommas(thresholds.expectedProcessedCountPerVcpu)}
                  valueColor="#1a73e8"
                />
                <InfoRow
                  label="Expected GB/vCPU"
                  value={dataSizeFormatter(thresholds.expectedProcessedSizeGbPerVcpu)}
                  valueColor="#1a73e8"
                />
              </Stack>
            </>
          ) : null}

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Review Status
            </Typography>
            <InfoRow
              label="Status"
              value={notification.isAcknowledged ? 'Acknowledged' : notification.isRead ? 'Read' : 'Open'}
              valueColor={notification.isAcknowledged ? '#1a73e8' : notification.isRead ? '#455a64' : '#8a4b10'}
            />
            {notification.readBy && (
              <>
                <InfoRow label="Read by" value={notification.readBy} />
                {notification.readAt && <InfoRow label="Read at" value={dateFormatter(notification.readAt)} />}
              </>
            )}
            {notification.isAcknowledged && (
              <>
                <InfoRow label="Acknowledged by" value={notification.acknowledgedBy || '--'} valueColor="#1a73e8" />
                {notification.acknowledgedAt && <InfoRow label="Acknowledged at" value={dateFormatter(notification.acknowledgedAt)} />}
              </>
            )}
          </Stack>

          {notification.reviewStatus === 'Resolved' || notification.resolvedBy ? (
            <>
              <Divider />
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                  Resolution Details
                </Typography>
                <InfoRow label="Resolved by" value={notification.resolvedBy} valueColor="#2e7d32" />
                <InfoRow label="Resolved at" value={dateFormatter(notification.resolvedAt)} />
                {notification.resolutionNotes ? (
                  <Box>
                    <Typography variant="body2" sx={{ color: tokens.colors.textMuted }}>
                      Notes
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, marginTop: 0.5 }}>
                      {notification.resolutionNotes}
                    </Typography>
                  </Box>
                ) : null}
              </Stack>
            </>
          ) : null}

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Timing & Tracking
            </Typography>
            <InfoRow label="Cluster" value={notification.cluster} valueColor={severityColor} />
            <InfoRow label="Created at" value={dateFormatter(notification.createdAt)} />
            {notification.dataTimestamp && (
              <InfoRow label="Data timestamp" value={dateFormatter(notification.dataTimestamp)} />
            )}
            {notification.alertDetails?.alertTimestamp && (
              <InfoRow label="Alert timestamp" value={dateFormatter(notification.alertDetails.alertTimestamp)} />
            )}
            <InfoRow label="Related Alert ID" value={notification.relatedAlertId} />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default NotificationDetailsCard;
